



































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { IFilter, ISorter } from "@/models/tables";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import { TemplateTypes } from "@/models/notifications/templates";
import eventsMap from "@/components/app/global/timelineComponent/events";
import { mapState } from "vuex";
import { IState } from "@/store";
import {
  INotificationCategory,
  INotificationChannel,
  INotificationRecipientType,
  INotificationHook,
  INotificationTemplate
} from "@/models/notifications";
import { FilterOperators, FilterTypes } from "@/data/table";
import i18n from "@/i18n";
import { NotificationChannelCodes } from "@/data/enums/notifications";

@Component({
  components: {
    "actions-table-row": () =>
      import("@/components/app/admin/automation/actionTableRow.vue")
  },
  computed: {
    ...mapState({
      channels(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.NOTIFICATIONS_CHANNELS
        });
      },
      recipientTypes(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.NOTIFICATIONS_RECIPIENT_TYPES
        });
      },
      categories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.NOTIFICATIONS_CATEGORIES
        });
      }
    })
  }
})
export default class AutomationActionsTable extends Mixins(
  FilteringSortingTableStore
) {
  // Props
  @Prop({ required: false, default: false })
  public showCategory!: boolean;

  @Prop({ default: null })
  public channelCode!: NotificationChannelCodes;

  @Prop({ default: null })
  public hookId!: INotificationHook["id"];

  // Data
  public dataModule: DataModules = DataModules.NOTIFICATIONS_TEMPLATES;
  public channels!: INotificationChannel[];
  public recipientTypes!: INotificationRecipientType[];
  public categories!: INotificationCategory[];

  public sorters: ISorter[] = [
    {
      field: "name",
      label: this.$t("_.name")
    },
    {
      field: "created_at",
      label: this.$t("_.date_added")
    }
  ];

  public filters: IFilter[] = [
    {
      key: "name",
      label: i18n.t("_.name"),
      type: FilterTypes.STRING,
      operator: FilterOperators.EQUAL
    },
    {
      key: "default",
      label: i18n.t("_._default"),
      type: FilterTypes.SELECT_RADIO,
      value: "1",
      operator: FilterOperators.EQUAL
    },
    {
      key: "enabled",
      label: i18n.t("_.enabled"),
      type: FilterTypes.SELECT_RADIO,
      value: "1",
      operator: FilterOperators.EQUAL
    },
    {
      key: "channel.code",
      label: i18n.t("_.channel"),
      type: FilterTypes.SELECT,
      optionsFunc: this.getChannelFilterOptions,
      operator: FilterOperators.EQUAL
    },
    {
      key: "category_id",
      label: i18n.t("_.category"),
      type: FilterTypes.SELECT,
      optionsFunc: this.getCategoryFilterOptions,
      operator: FilterOperators.EQUAL
    },
    {
      key: "recipient_type_id",
      label: i18n.t("_.recipient_type"),
      type: FilterTypes.SELECT,
      optionsFunc: this.getRecipientTypeFilterOptions,
      operator: FilterOperators.EQUAL
    },
    {
      key: "mappings.hook_id",
      label: i18n.t("_.hook"),
      type: FilterTypes.SELECT,
      optionsFunc: this.getHookFilterOptions,
      operator: FilterOperators.EQUAL
    },
    {
      key: "created_at",
      label: i18n.t("_.created"),
      type: FilterTypes.DATE,
      operator: FilterOperators.EQUAL
    }
  ];

  public guardFunctionalities = [
    "get_notification_channel",
    "list_notification_templates"
  ];

  public async created() {
    if (!this.contentIsAvailable) {
      return;
    }
    this.apiProps = { with: "category,mappings.hook,recipient_type,channel" };
    this.getData();
  }

  @Watch("channelCode")
  public channelChanged() {
    this.getData();
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public async getChannels() {
    return this.$store.dispatch(
      `data/${DataModules.NOTIFICATIONS_CHANNELS}/list`,
      {
        vm: this,
        params: {
          order: "id",
          limit: 0
        }
      }
    );
  }

  public async getRecipientTypes() {
    return this.$store.dispatch(
      `data/${DataModules.NOTIFICATIONS_RECIPIENT_TYPES}/list`,
      {
        vm: this,
        params: {
          limit: 0,
          order: "name"
        }
      }
    );
  }

  public async getCategories() {
    return this.$store.dispatch(
      `data/${DataModules.NOTIFICATIONS_CATEGORIES}/list`,
      {
        vm: this,
        params: {
          limit: 0,
          order: "name",
          with: "hooks"
        }
      }
    );
  }

  public get silentFilters() {
    const silentFilters = {};
    if (this.channelCode) {
      silentFilters["filter[channel.code]"] = this.channelCode;
    }
    if (this.hookId) {
      silentFilters["filter[mappings.hook_id]"] = this.hookId;
    }
    return silentFilters;
  }

  // overrides mixin method
  public getStoreParams(params: any) {
    return this.$_.merge(params, {
      params: {
        "filter[type]": TemplateTypes.NOTIFICATION,
        ...params.filter,
        ...this.silentFilters
      }
    });
  }

  public getTemplateToRoute(template) {
    return {
      name: this.$route.path.includes("email-templates")
        ? "adminEditEmailTemplate"
        : "adminAutomationAction",
      params: {
        templateId: template.id
      }
    };
  }

  public viewAllActionsForHook(hookId: string) {
    this.$store.dispatch("ui/open/slideModal", {
      config: {
        component: () =>
          import("@/components/app/admin/automation/triggerModal.vue"),
        props: { hookId },
        width: 540,
        events: {
          "actions-updated": () => this.getData({ ignoreStored: true })
        }
      }
    });
  }

  public async getChannelFilterOptions() {
    await this.getChannels();
    return this.channels.map(channel => {
      return { value: channel.code, label: channel.name };
    });
  }

  public async getCategoryFilterOptions() {
    await this.getCategories();
    return this.categories.map(category => {
      return { value: category.id.toString(), label: category.name };
    });
  }

  public async getRecipientTypeFilterOptions() {
    await this.getRecipientTypes();
    return this.recipientTypes.map(recipientType => {
      return {
        value: recipientType.id.toString(),
        label: recipientType.name
      };
    });
  }

  public mapHookName(hook) {
    return eventsMap?.[hook?.code]?.name || hook.name;
  }

  public async getHookFilterOptions() {
    await this.getCategories();
    const hookOptions: any[] = [];
    this.categories.forEach(category => {
      hookOptions.push({
        name: category.name,
        options: this.$_.orderBy(
          (category.hooks || []).map(hook => {
            return {
              value: hook.id.toString(),
              label: this.mapHookName(hook)
            };
          }),
          "label",
          "asc"
        )
      });
    });

    return hookOptions;
  }

  public async deleteTemplate(templateId: INotificationTemplate["id"]) {
    if (!this.$userCan("delete_notification_template")) {
      return;
    }
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.action") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.onDeleteConfirmed(templateId);
            confirm.close();
          }
        }
      }
    });
  }

  public async onDeleteConfirmed(
    templateId: INotificationTemplate["id"]
  ): Promise<any> {
    if (!this.$userCan("delete_notification_template")) {
      return;
    }
    this.table.loading = true;
    try {
      await this.$store.dispatch(`data/${this.dataModule}/remove`, {
        id: templateId
      });
      this.$emit("actions-updated");
      this.getData({ ignoreStored: true });
      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_removed", {
          object: this.$t("_.action") as string
        }) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.table.loading = false;
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.NOTIFICATIONS_CHANNELS,
      vm: this
    });
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.NOTIFICATIONS_CATEGORIES,
      vm: this
    });
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.NOTIFICATIONS_RECIPIENT_TYPES,
      vm: this
    });
  }
}
